<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  onDeactivated,
  ref,
  nextTick,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TemplateBuilder from './TemplateBuilder.vue';
import TemplatePreviewPanel from './components/TemplatePreviewPanel.vue';
import TemplateUpdateModal from './components/TemplateUpdateModal.vue';
import MessageTemplatesAPI from 'dashboard/api/messageTemplates';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const templateBuilderRef = ref(null);
const showMobilePreview = ref(false);
const showUpdateModal = ref(false);
const pendingUpdateData = ref(null);
const templateId = computed(() => router.currentRoute.value.params.templateId);
const currentTemplate = ref(null);
const isLoadingTemplate = ref(false);
const originalTemplateSnapshot = ref(null);

const isTemplateBuilderValid = computed(() => {
  return !!templateBuilderRef.value?.isValidTemplate;
});

const isEditMode = computed(() => {
  return !!templateId.value;
});

const builderConfig = computed(() => {
  return store.getters['messageTemplates/getBuilderConfig'];
});

const hasChanges = computed(() => {
  if (
    !isEditMode.value ||
    !originalTemplateSnapshot.value ||
    !templateBuilderRef.value
  ) {
    return false;
  }

  const current = {
    body: templateBuilderRef.value.previewData?.body?.text || '',
    header: templateBuilderRef.value.previewData?.header || {},
    footer: templateBuilderRef.value.previewData?.footer || {},
    buttons: templateBuilderRef.value.previewData?.buttons || [],
    variableMappings: templateBuilderRef.value.variableMappings || {},
    parameterType: templateBuilderRef.value.parameterType || 'positional',
  };

  return (
    JSON.stringify(current) !== JSON.stringify(originalTemplateSnapshot.value)
  );
});

const canUpdate = computed(() => {
  return hasChanges.value;
});

const isLoading = computed(() => {
  const uiFlags = store.getters['messageTemplates/getUIFlags'];
  return uiFlags.isCreating || uiFlags.isUpdating;
});

const isDeleting = computed(() => {
  const uiFlags = store.getters['messageTemplates/getUIFlags'];
  return uiFlags.isDeleting;
});

const previewData = computed(() => {
  return templateBuilderRef.value?.previewData || {};
});

const variableMappings = computed(() => {
  return templateBuilderRef.value?.variableMappings || {};
});

const validationResult = computed(() => {
  return (
    templateBuilderRef.value?.validationResult || {
      isValid: true,
      errors: [],
      warnings: [],
    }
  );
});

const toggleMobilePreview = () => {
  showMobilePreview.value = !showMobilePreview.value;
};

const goBack = () => {
  router.push({ name: 'templates_list' });
};

const resetBuilderState = () => {
  templateBuilderRef.value?.resetBuilderState();
  store.dispatch('messageTemplates/resetBuilderConfig');
};

const loadTemplateData = async () => {
  if (!templateId.value) {
    return;
  }

  isLoadingTemplate.value = true;

  try {
    // Fetch template directly from API to get full details including content and metadata
    const response = await MessageTemplatesAPI.show(templateId.value);
    const template = response.data;

    if (!template) {
      useAlert(t('SETTINGS.TEMPLATES.API.FETCH_ERROR'));
      goBack();
      return;
    }

    currentTemplate.value = template;

    // Set builder config
    await store.dispatch('messageTemplates/setBuilderConfig', {
      name: template.name,
      language: template.language,
      category: template.category,
      channelType: template.channel_type,
      inboxId: template.inbox_id,
      templateId: template.id,
    });

    // Stop loading to allow component to render
    isLoadingTemplate.value = false;

    // Wait for DOM updates and ensure component is mounted
    await nextTick();
    await nextTick(); // Double tick to ensure all child components are ready

    // Try to load with multiple retries for reliability
    const loadWithRetry = (attempt = 0) => {
      if (
        templateBuilderRef.value &&
        templateBuilderRef.value.loadTemplateData
      ) {
        templateBuilderRef.value.loadTemplateData(template);

        // Capture snapshot after loading for change detection
        setTimeout(() => {
          if (templateBuilderRef.value) {
            originalTemplateSnapshot.value = {
              body: templateBuilderRef.value.previewData?.body?.text || '',
              header: templateBuilderRef.value.previewData?.header || {},
              footer: templateBuilderRef.value.previewData?.footer || {},
              buttons: templateBuilderRef.value.previewData?.buttons || [],
              variableMappings: templateBuilderRef.value.variableMappings || {},
              parameterType:
                templateBuilderRef.value.parameterType || 'positional',
            };
          }
        }, 100);
      } else if (attempt < 5) {
        // Retry up to 5 times with increasing delays
        setTimeout(() => loadWithRetry(attempt + 1), 50 * (attempt + 1));
      }
    };

    loadWithRetry();
  } catch (error) {
    useAlert(t('SETTINGS.TEMPLATES.API.FETCH_ERROR'));
    goBack();
    isLoadingTemplate.value = false;
  }
};

const handleSave = async () => {
  if (!isTemplateBuilderValid.value) return;

  try {
    const templateConfig = store.getters['messageTemplates/getBuilderConfig'];

    // Add validation to check if templateBuilderRef is available
    if (!templateBuilderRef.value) {
      useAlert('Template builder is not ready. Please try again.');
      return;
    }

    const components = templateBuilderRef.value.generateComponents();
    const mappings = templateBuilderRef.value.getVariableMappings();
    const location = templateBuilderRef.value.getLocationMetadata();

    const templateData = {
      name: templateConfig.name,
      language: templateConfig.language,
      category: templateConfig.category,
      channel_type: templateConfig.channelType,
      inbox_id: templateConfig.inboxId,
      parameter_format: templateBuilderRef.value.parameterType,
      content: {
        components,
      },
      metadata: {
        variable_mappings: mappings,
        ...(location && { location }),
      },
    };

    if (isEditMode.value) {
      // Show modal to ask user about update strategy
      pendingUpdateData.value = templateData;
      showUpdateModal.value = true;
      return; // Exit here, will continue in handleUpdateModalContinue
    }
    // Create new template
    await store.dispatch('messageTemplates/create', templateData);
    useAlert(t('SETTINGS.TEMPLATES.API.SUCCESS_MESSAGE'));

    goBack();
  } catch (error) {
    if (isEditMode.value) {
      useAlert(t('SETTINGS.TEMPLATES.API.UPDATE_ERROR_MESSAGE'));
    } else {
      useAlert(t('SETTINGS.TEMPLATES.API.ERROR_MESSAGE'));
    }
  }
};

const handleUpdateModalContinue = async ({ option, newName }) => {
  showUpdateModal.value = false;

  if (!pendingUpdateData.value) return;

  try {
    const templateData = { ...pendingUpdateData.value };

    // If user chose new name, treat it as a "Save As" / Create New operation
    if (option === 'new_name' && newName) {
      templateData.name = newName;

      // Create new template
      await store.dispatch('messageTemplates/create', templateData);

      useAlert(t('SETTINGS.TEMPLATES.API.SUCCESS_MESSAGE'));
      goBack();
      return;
    }

    // Proceed with update (only if name hasn't changed)
    await store.dispatch('messageTemplates/update', {
      id: currentTemplate.value.id,
      ...templateData,
    });

    useAlert(t('SETTINGS.TEMPLATES.API.UPDATE_SUCCESS_MESSAGE'));
    goBack();
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      t('SETTINGS.TEMPLATES.API.UPDATE_ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    pendingUpdateData.value = null;
  }
};

const handleUpdateModalCancel = () => {
  showUpdateModal.value = false;
  pendingUpdateData.value = null;
};

const showDeleteConfirm = ref(false);

const handleDelete = () => {
  showDeleteConfirm.value = true;
};

const confirmDelete = async () => {
  try {
    await store.dispatch('messageTemplates/delete', currentTemplate.value.id);
    useAlert(t('SETTINGS.TEMPLATES.DELETE.API.SUCCESS_MESSAGE'));
    goBack();
  } catch (error) {
    useAlert(t('SETTINGS.TEMPLATES.DELETE.API.ERROR_MESSAGE'));
  } finally {
    showDeleteConfirm.value = false;
  }
};

const cancelDelete = () => {
  showDeleteConfirm.value = false;
};

// Watch for template ID changes (when navigating between templates)
watch(
  () => templateId.value,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      // Only reset the builder component, not the store config
      if (templateBuilderRef.value) {
        templateBuilderRef.value.resetBuilderState();
      }
      // Load new template data
      await loadTemplateData();
    }
  }
);

onMounted(async () => {
  if (isEditMode.value) {
    // Load template data for editing/viewing
    await loadTemplateData();
  } else {
    // Check if builder config exists for create mode
    const config = store.getters['messageTemplates/getBuilderConfig'];
    if (!config.name) {
      router.push({ name: 'templates_list' });
    }
  }
});

onDeactivated(resetBuilderState);
onUnmounted(resetBuilderState);
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-slate-700"
    >
      <!-- Breadcrumb -->
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="text-slate-400 hover:text-slate-200 text-sm font-medium transition-colors"
          @click="goBack"
        >
          {{ t('SETTINGS.TEMPLATES.HEADER.TITLE') }}
        </button>
        <span class="i-lucide-chevron-right size-4 text-slate-600" />
        <span class="text-sm text-slate-400">
          {{
            isEditMode
              ? t('SETTINGS.TEMPLATES.BUILDER.EDIT_TEMPLATE')
              : t('SETTINGS.TEMPLATES.BUILDER.BUILD_TEMPLATE')
          }}
        </span>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-3">
        <NextButton
          v-if="isEditMode"
          ruby
          faded
          label="Delete"
          @click="handleDelete"
        />
        <NextButton
          slate
          faded
          :label="isEditMode ? 'Discard changes' : 'Discard'"
          @click="goBack"
        />
        <NextButton
          blue
          :label="isEditMode ? 'Update template' : 'Save template'"
          :disabled="
            !isTemplateBuilderValid || isLoading || (isEditMode && !canUpdate)
          "
          :is-loading="isLoading"
          @click="handleSave"
        />
      </div>
    </div>

    <!-- Template Name Title Section -->
    <div
      v-if="builderConfig.name"
      class="px-6 py-4 border-b border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-file-text size-5 text-n-slate-11" />
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ builderConfig.name }}
          </h2>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ builderConfig.language?.toUpperCase() }} •
            {{ builderConfig.category }}
            <template v-if="isEditMode && currentTemplate?.status">
              •
              <span
                :class="{
                  'text-n-green-11': currentTemplate.status === 'APPROVED',
                  'text-n-amber-11': currentTemplate.status === 'PENDING',
                  'text-n-ruby-11': currentTemplate.status === 'REJECTED',
                }"
              >
                {{ currentTemplate.status }}
              </span>
            </template>
            <template v-else-if="!isEditMode">
              •
              <span class="text-n-slate-11">
                {{ t('SETTINGS.TEMPLATES.STATUS.DRAFT') }}
              </span>
            </template>
          </p>
        </div>
      </div>
    </div>

    <!-- Edit Mode Info Banner -->
    <div
      v-if="isEditMode && currentTemplate"
      class="px-6 py-4 border-b bg-n-amber-2 border-n-amber-5"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-alert-triangle size-5 text-n-amber-9" />
        <div class="flex-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('SETTINGS.TEMPLATES.BUILDER.EDIT_MODE_WARNING') }}
          </p>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('SETTINGS.TEMPLATES.BUILDER.EDIT_MODE_DESCRIPTION') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Loading State -->
      <div
        v-if="isLoadingTemplate"
        class="flex items-center justify-center w-full"
      >
        <woot-loading-state :message="$t('SETTINGS.TEMPLATES.LOADING')" />
      </div>

      <!-- Two-column layout on desktop, single column on mobile -->
      <div v-else class="flex-1 lg:grid lg:grid-cols-[60%_40%] overflow-hidden">
        <!-- Builder Panel -->
        <div class="flex-1 py-12 px-8 lg:px-16 overflow-y-auto">
          <div class="max-w-5xl mx-auto">
            <TemplateBuilder ref="templateBuilderRef" />
          </div>
        </div>

        <!-- Preview Panel (Desktop only - always visible) -->
        <div class="hidden lg:flex lg:flex-col border-l border-slate-700">
          <!-- Preview Section -->
          <div class="overflow-y-auto">
            <TemplatePreviewPanel
              :template-data="previewData"
              :variable-mappings="variableMappings"
            />
          </div>

          <!-- Guide Section -->
          <div class="border-t border-n-weak bg-n-solid-1">
            <div class="px-4 py-3 border-b border-n-weak">
              <h4 class="text-sm font-medium text-n-slate-12">Guide</h4>
            </div>
            <div class="p-4">
              <div
                v-if="validationResult.isValid"
                class="flex items-center gap-2 text-sm text-n-green-11"
              >
                <span class="i-lucide-check-circle size-4 flex-shrink-0" />
                <span>{{
                  $t('SETTINGS.TEMPLATES.BUILDER.PREVIEW.READY_TO_SAVE')
                }}</span>
              </div>
              <div
                v-else
                class="flex items-center gap-2 text-sm text-n-slate-11"
              >
                <span class="i-lucide-info size-4 flex-shrink-0" />
                <span>Please complete the following points to submit the
                  template</span>
              </div>
            </div>
          </div>

          <!-- Validation Hints Section -->
          <div
            v-if="
              validationResult.errors.length > 0 ||
              validationResult.warnings.length > 0
            "
            class="border-t border-n-weak bg-n-solid-1 p-4 max-h-48 overflow-y-auto"
          >
            <!-- Errors -->
            <div v-if="validationResult.errors.length > 0" class="space-y-2">
              <div
                v-for="(error, index) in validationResult.errors"
                :key="`error-${index}`"
                class="flex items-start gap-2 text-sm"
              >
                <span
                  class="i-lucide-circle size-2 text-n-ruby-9 mt-1.5 flex-shrink-0"
                />
                <span class="text-n-slate-11">{{ error }}</span>
              </div>
            </div>

            <!-- Warnings -->
            <div
              v-if="validationResult.warnings.length > 0"
              class="space-y-2"
              :class="{ 'mt-3': validationResult.errors.length > 0 }"
            >
              <div
                v-for="(warning, index) in validationResult.warnings"
                :key="`warning-${index}`"
                class="flex items-start gap-2 text-sm"
              >
                <span
                  class="i-lucide-circle size-2 text-n-amber-9 mt-1.5 flex-shrink-0"
                />
                <span class="text-n-slate-11">{{ warning }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Preview Button (Fixed bottom-right) -->
    <button
      class="lg:hidden fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-n-brand text-white rounded-full shadow-lg hover:bg-n-brand/90 transition-colors"
      @click="toggleMobilePreview"
    >
      <span class="i-lucide-eye size-5" />
      <span class="font-medium">{{
        t('SETTINGS.TEMPLATES.PREVIEW.BUTTON')
      }}</span>
    </button>

    <!-- Mobile Preview Overlay -->
    <div
      v-if="showMobilePreview"
      class="lg:hidden fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
      @click="toggleMobilePreview"
    >
      <div
        class="absolute inset-4 bg-n-solid-1 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        @click.stop
      >
        <!-- Overlay Header -->
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('SETTINGS.TEMPLATES.PREVIEW.TITLE') }}
          </h3>
          <button
            class="i-lucide-x size-6 text-n-slate-11 hover:text-n-slate-12"
            @click="toggleMobilePreview"
          />
        </div>

        <!-- Overlay Content -->
        <div class="flex-1 overflow-y-auto">
          <TemplatePreviewPanel
            :template-data="previewData"
            :variable-mappings="variableMappings"
          />
        </div>
      </div>
    </div>

    <!-- Update Modal -->
    <TemplateUpdateModal
      :show="showUpdateModal"
      :original-name="currentTemplate?.name || ''"
      @continue="handleUpdateModalContinue"
      @cancel="handleUpdateModalCancel"
    />

    <!-- Delete Confirmation Modal -->
    <woot-confirm-delete-modal
      v-if="showDeleteConfirm"
      v-model:show="showDeleteConfirm"
      :title="
        $t('SETTINGS.TEMPLATES.DELETE.CONFIRM.TITLE', {
          templateName: currentTemplate?.name || '',
        })
      "
      :message="$t('SETTINGS.TEMPLATES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="
        $t('SETTINGS.TEMPLATES.DELETE.CONFIRM.YES') +
        ' ' +
        (currentTemplate?.name || '')
      "
      :reject-text="$t('SETTINGS.TEMPLATES.DELETE.CONFIRM.NO')"
      :confirm-value="currentTemplate?.name || ''"
      :confirm-place-holder-text="
        $t('SETTINGS.TEMPLATES.DELETE.CONFIRM.PLACE_HOLDER', {
          templateName: currentTemplate?.name || '',
        })
      "
      :is-loading="isDeleting"
      @on-confirm="confirmDelete"
      @on-close="cancelDelete"
    />
  </div>
</template>
